<template>
    <div class="editr">
        <div class="editr--toolbar">
            <Btn v-for="(module,i) in modules" :module="module" :options="mergedOptions" :key="module.title + i" :ref="'btn-'+module.title" :title="translate(module.description) || ''" />
        </div>
        <div class="editr--content" v-show="!source" ref="content" contenteditable="true" tabindex="1" :placeholder="placeholder"></div>
        <PrismEditor v-show="source" :code="textareaModel" language="html" @change="setCode" />
    </div>
</template>

<script>
import PrismEditor from 'vue-prism-editor'
import debounce from "debounce";
import Btn from "./Button.vue";

import bold from "./modules/bold.js";
import italic from "./modules/italic.js";
import underline from "./modules/underline.js";
import fontsize from "./modules/fontsize.vue";
import fontcolor from "./modules/fontcolor.vue";
import backgroundcolor from "./modules/backgroundcolor.vue";

import alignLeft from "./modules/alignLeft.js"
import alignCenter from "./modules/alignCenter.js"
import alignRight from "./modules/alignRight.js"


import headings from "./modules/headings.vue";
import hyperlink from "./modules/hyperlink.vue";
import code from "./modules/code.js";
import list_ordered from "./modules/list_ordered.js";
import list_unordered from "./modules/list_unordered.js";

import image from "./modules/image.vue";
import table from "./modules/table.vue";

import removeFormat from "./modules/removeFormat.js";
import removeStyles from "./modules/removeStyles.js";
import removeHtmlTags from "./modules/removeHtmlTags.js";
import viewSource from "./modules/viewSource.js";

import separator from "./modules/separator.js";

const modules = [
    bold, italic, underline, fontsize, fontcolor, backgroundcolor, separator,
    alignLeft, alignCenter, alignRight, separator,
    headings, hyperlink, code,
    list_ordered, list_unordered, separator,
    image, table, separator,
    removeFormat, removeStyles, removeHtmlTags, separator,
    viewSource
];

import './style.scss'

export default
{
    model:
    {
        prop: "html",
        event: "html"
    },
    props:
    {
        html:
        {
            type: String,
            default: ""
        },
        placeholder:
        {
            type: String,
            default: "Enter text..."
        },
        options: Object,
        translateMethod: null
    },
    components:
    {
        Btn,
        PrismEditor
    },
    data()
    {
        return {
            textareaModel: '',
            source: false,
            sourceCode: '',
            selection: "",
            selHtml: ''
        }
    },
    computed:
    {
        mergedOptions()
        {
            return {
                image:
                {
                    uploadURL: "None",
                    dropzoneOptions: {}
                },
                hideModules: {},
                paragraphSeparator: "div",
                maxHeight: undefined,
                customModules: [],
                ...this.options
            }
        },
        modules()
        {
            const customIcons = this.mergedOptions.iconOverrides;

            return modules
                .filter(
                    m => this.mergedOptions.hideModules === undefined ||
                    !this.mergedOptions.hideModules[m.title]
                )
                .map(mod =>
                {
                    if (customIcons !== undefined && customIcons[mod.title] !== undefined)
                    {
                        mod.icon = customIcons[mod.title];
                    }
                    return mod;
                })
                .concat(this.mergedOptions.customModules);
        },
        btnsWithDashboards()
        {
            if (this.modules)
                return this.modules.filter(m => m.render);
            return [];
        },
        innerHTML:
        {
            get()
            {
                if (this.$refs.content) return this.$refs.content.innerHTML;
                return '';
            },

            set(html)
            {
                if (this.$refs.content.innerHTML !== html)
                {
                    this.$refs.content.innerHTML = html;
                }
            }
        }
    },
    methods:
    {
        translate(key)
        {
            var me = this;
            if (me.translateMethod != null)
            {
                return me.translateMethod(key);
            }
            return key;
        },
        getSelectionHtml()
        {
            var html = "";
            if (typeof window.getSelection != "undefined") {
                var sel = window.getSelection();
                var container = document.createElement("div");
                container.appendChild(sel.getRangeAt(0).cloneContents());
                html = container.innerHTML;
            } else if (typeof document.selection != "undefined") {
                if (document.selection.type == "Text") {
                    html = document.selection.createRange().htmlText;
                }
            }

            return html;
        },
        saveSelection()
        {
            if (window.getSelection !== undefined)
            {
                this.selection = window.getSelection();
                if (this.selection.getRangeAt && this.selection.rangeCount)
                {
                    return this.selection.getRangeAt(0);
                }
            }
            else if (document.selection && document.selection.createRange)
            {
                return document.selection.createRange();
            }
            return null;
        },
        restoreSelection(range)
        {
            if (range)
            {
                if (window.getSelection !== undefined)
                {
                    this.selection = window.getSelection();
                    this.selection.removeAllRanges();
                    this.selection.addRange(range);
                }
                else if (document.selection && range.select)
                    range.select();
            }
        },
        clearSelection()
        {
            this.selection = null;
            const selection = window.getSelection();

            if (selection)
            {
                if (selection.empty !== undefined)
                {
                    selection.empty();
                }
                if (selection.removeAllRanges !== undefined)
                {
                    selection.removeAllRanges();
                }
            }
        },
        exec(cmd, arg, sel)
        {
            var me = this;
            if (cmd == 'viewSource')
            {
                this.textareaModel = this.$refs.content.innerHTML;
                if (me.source == true)
                {
                    this.$refs.content.innerHTML = me.sourceCode;
                    this.emit();
                    me.$nextTick(() =>
                    {
                        me.source = false;
                    })
                }
                else
                {
                    me.source = true;
                }
            }
            else if (cmd == 'removeFormat')
            {
                if (document.selection)
                {
                    var range = document.body.createTextRange();
                    range.moveToElementText(this.$refs.content);
                    range.select();
                }
                else if (window.getSelection)
                {
                    window.getSelection().removeAllRanges();
                    var range = document.createRange();
                    range.selectNodeContents(this.$refs.content);
                    window.getSelection().removeAllRanges();
                    window.getSelection().addRange(range);
                }

                sel !== false && this.selection && this.restoreSelection(this.selection);
                document.execCommand(cmd, false, arg || "");
                this.clearSelection();
                this.$nextTick(this.emit);
            }
            else if (cmd == 'removeStyles')
            {
                var myString = this.$refs.content.innerHTML;
                myString = myString.replace(/(<[^>]+) style=".*?"/ig, '$1');
                myString = myString.replace(/(<[^>]+) class=".*?"/ig, '$1');
                this.$refs.content.innerHTML = myString;
                this.emit();
            }
            else if (cmd == 'removeHtmlTags')
            {
                var myString = this.$refs.content.innerHTML;
                myString = myString.replace(/<\/?[^>]+(>|$)/g, "");
                this.$refs.content.innerHTML = myString;
                this.emit();
            }
            else
            {
                sel !== false && this.selection && this.restoreSelection(this.selection);
                document.execCommand(cmd, false, arg || "");
                this.clearSelection();
                this.$nextTick(this.emit);
            }
        },
        onDocumentClick(e)
        {
            for (let i = 0; i < this.btnsWithDashboards.length; i++)
            {
                const btn = this.$refs[`btn-${this.btnsWithDashboards[i].title}`][0];
                if (btn && btn.showDashboard && !btn.$el.contains(e.target))
                    btn.closeDashboard();
            }
        },
        emit()
        {
            if (this.source) this.textareaModel = this.$refs.content.innerHTML;
            this.$emit("html", this.$refs.content.innerHTML);
            this.$emit("change", this.$refs.content.innerHTML);
        },
        onInput: debounce(function()
        {
            this.emit();
        }, 300),
        onFocus()
        {
            document.execCommand("defaultParagraphSeparator", false, this.mergedOptions.paragraphSeparator)
        },
        onMouseUp()
        {
            // save focus to restore it later
            this.selHtml = this.getSelectionHtml();
            this.selection = this.saveSelection();
            this.$emit("blur", this.$refs.content);
        },
        onPaste(e)
        {
            e.preventDefault();

            // get a plain representation of the clipboard
            var text = e.clipboardData.getData("text/plain");

            // insert that plain text text manually
            document.execCommand("insertHTML", false, text);
        },
        syncHTML()
        {
            if (this.html !== this.$refs.content.innerHTML)
                this.innerHTML = this.html;
        },
        setCode(val)
        {
            this.sourceCode = val;
        }
    },
    mounted()
    {
        this.unwatch = this.$watch("html", this.syncHTML,
        {
            immediate: true
        });

        document.addEventListener("click", this.onDocumentClick);

        this.$refs.content.addEventListener("focus", this.onFocus);
        this.$refs.content.addEventListener("input", this.onInput);
        this.$refs.content.addEventListener("mouseup", this.onMouseUp,
        {
            capture: true
        });

        if (this.mergedOptions.forcePlainTextOnPaste === true)
        {
            this.$refs.content.addEventListener("paste", this.onPaste);
        }

        this.$refs.content.style.maxHeight = this.mergedOptions.maxHeight;
    },
    beforeDestroy()
    {
        this.unwatch();
        document.removeEventListener("click", this.onDocumentClick);

        this.$refs.content.removeEventListener("mouseup", this.onMouseUp);
        this.$refs.content.removeEventListener("input", this.onInput);
        this.$refs.content.removeEventListener("focus", this.onFocus);
    },
    watch:
    {
        textareaModel(val)
        {
            this.sourceCode = val;
            this.$refs.content.innerHTML = val;
            this.emit();
        }
    }
}
</script>
